import * as React from "react"
import { ChevronDown, X, Plus } from "lucide-react"
import { cn } from "@/lib/utils"

const RPE_OPTIONS = [
  { value: 6, label: "6", description: "Easy (4+ reps left)" },
  { value: 7, label: "7", description: "Moderate (3 reps left)" },
  { value: 8, label: "8", description: "Hard (2 reps left)" },
  { value: 9, label: "9", description: "Very hard (1 rep left)" },
  { value: 10, label: "10", description: "Max effort (0 reps left)" },
] as const

export interface RpeSelectProps {
  value: number | null
  onChange: (value: number | null) => void
  autoOpen?: boolean
  className?: string
}

export function RpeSelect({ value, onChange, autoOpen = false, className }: RpeSelectProps) {
  const [isOpen, setIsOpen] = React.useState(autoOpen)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const listboxRef = React.useRef<HTMLDivElement>(null)

  // Handle autoOpen prop change
  React.useEffect(() => {
    if (autoOpen) {
      setIsOpen(true)
    }
  }, [autoOpen])

  // Close on outside click
  React.useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    // Delay to avoid immediate close from the click that opened it
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside)
    }, 0)

    return () => {
      clearTimeout(timer)
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  // Close on escape
  React.useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false)
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [isOpen])

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault()
        setIsOpen(true)
      }
      return
    }

    const currentIndex = value ? RPE_OPTIONS.findIndex((opt) => opt.value === value) : -1

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        if (currentIndex < RPE_OPTIONS.length - 1) {
          onChange(RPE_OPTIONS[currentIndex + 1].value)
        } else if (currentIndex === -1) {
          onChange(RPE_OPTIONS[0].value)
        }
        break
      case "ArrowUp":
        e.preventDefault()
        if (currentIndex > 0) {
          onChange(RPE_OPTIONS[currentIndex - 1].value)
        }
        break
      case "Enter":
      case " ":
        e.preventDefault()
        setIsOpen(false)
        break
    }
  }

  const handleSelect = (selectedValue: number) => {
    onChange(selectedValue)
    setIsOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      {/* Trigger chip - matches TuneChip dimensions */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={value ? `RPE ${value} out of 10` : "Add target RPE"}
        className={cn(
          "h-10 rounded-xl transition-colors cursor-pointer",
          "flex items-center gap-2 px-4",
          "w-full",
          "bg-[var(--surface)] hover:bg-[var(--surface-strong)]",
          "focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]",
          value === null ? "justify-center" : "justify-between"
        )}
      >
        {value !== null ? (
          <>
            <div className="flex items-center gap-1">
              <span className="text-base font-semibold text-[var(--text-strong)]">
                {value}
              </span>
              <span className="text-sm font-semibold text-neutral-500">
                /10
              </span>
            </div>
            <ChevronDown
              className={cn(
                "w-4 h-4 text-neutral-500 transition-transform",
                isOpen && "rotate-180"
              )}
            />
          </>
        ) : (
          <div className="flex items-center gap-1.5 text-neutral-400">
            <Plus className="w-4 h-4" />
            <span className="text-sm font-medium">Add target RPE</span>
          </div>
        )}
      </button>

      {/* Dropdown popover */}
      {isOpen && (
        <div
          ref={listboxRef}
          role="listbox"
          aria-label="Select RPE value"
          aria-activedescendant={value ? `rpe-option-${value}` : undefined}
          className={cn(
            "absolute top-full left-0 mt-1.5 z-50",
            "bg-white rounded-2xl shadow-lg border border-neutral-200",
            "py-2 min-w-[240px]",
            "animate-in fade-in-0 zoom-in-95 duration-100"
          )}
        >
          {/* Header */}
          {/* <div className="px-3 pb-2 mb-1 border-b border-neutral-100">
            <span className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">
              Select RPE
            </span>
          </div> */}

          {/* Options */}
          <div className="px-1">
            {RPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                id={`rpe-option-${option.value}`}
                type="button"
                role="option"
                aria-selected={value === option.value}
                onClick={() => handleSelect(option.value)}
                className={cn(
                  "w-full text-left px-3 py-2.5 rounded-lg transition-colors",
                  "flex items-center gap-3",
                  "focus-visible:outline-none focus-visible:bg-neutral-100",
                  value === option.value
                    ? "bg-neutral-100 text-neutral-900"
                    : "text-neutral-600 hover:bg-neutral-50"
                )}
              >
                <span
                  className={cn(
                    "w-7 text-center font-semibold",
                    value === option.value ? "text-neutral-900" : "text-neutral-700"
                  )}
                >
                  {option.label}
                </span>
                <span
                  className={cn(
                    "text-sm flex-1",
                    value === option.value ? "text-neutral-700" : "text-neutral-500"
                  )}
                >
                  {option.description}
                </span>
                {value === option.value && (
                  <div className="w-1.5 h-1.5 rounded-full bg-neutral-900" />
                )}
              </button>
            ))}
          </div>

          {/* Clear option */}
          {value !== null && (
            <div className="px-1 pt-1 mt-1 border-t border-neutral-100">
              <button
                type="button"
                onClick={handleClear}
                className={cn(
                  "w-full text-left px-3 py-2 rounded-lg transition-colors",
                  "flex items-center gap-2",
                  "text-neutral-400 hover:text-neutral-600 hover:bg-neutral-50",
                  "focus-visible:outline-none focus-visible:bg-neutral-100"
                )}
              >
                <X className="w-4 h-4" />
                <span className="text-sm">Clear RPE</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

RpeSelect.displayName = "RpeSelect"
